import { useContext, useEffect, useRef, useState } from 'react';

import {
  ServiceFeaturesListType,
  isQuickQuoteUiEnabled,
} from 'contexts/serviceContext';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import {
  LoadCloneRecommendationDisposition,
  getLoadCloneRecommendations,
} from 'lib/api/cloneLoads';
import { getLoadBuildingSuggestion } from 'lib/api/getLoadBuildingSuggestion';
import { getQuickQuoteSuggestion } from 'lib/api/getQuickQuoteSuggestion';
import { getQuoteRequest } from 'lib/api/getQuoteRequest';
import getTruckList from 'lib/api/getTruckList';
import { Email } from 'types/Email';
import { Maybe } from 'types/UtilityTypes';
import { hasQuoteViewSuggestions } from 'utils/hasQuoteViewSuggestions';
import { normalizeSuggestions } from 'utils/normalizeTimezone';
import { filterInvalidSuggestions } from 'utils/suggestionValidators';

type UseQuoteViewSuggestionsPrefetchProps = {
  email: Maybe<Email>;
  enabled: boolean;
  serviceFeaturesEnabled: ServiceFeaturesListType;
};

/**
 * Prefetches Quote-view suggestion signals while the user is on Loads view so the
 * titlebar can pulse Quotes without mounting QuoteSidebar.
 */
export function useQuoteViewSuggestionsPrefetch({
  email,
  enabled,
  serviceFeaturesEnabled,
}: UseQuoteViewSuggestionsPrefetchProps) {
  const {
    currentState: { hasQuoteViewFeatureSuggestions },
    setCurrentState,
  } = useContext(SidebarStateContext);
  const resolvedRef = useRef(hasQuoteViewFeatureSuggestions);
  resolvedRef.current = hasQuoteViewFeatureSuggestions;
  const [cloneRevalidation, setCloneRevalidation] = useState(0);
  const previousCloningEnabledRef = useRef(
    serviceFeaturesEnabled.isLoadCloningEnabled
  );
  const forceRefreshRef = useRef(false);
  const cloneInFlightKeyRef = useRef<Maybe<string>>(null);
  const ordinarySignalRef = useRef<Maybe<boolean>>(null);
  const cloneSignalRef = useRef<Maybe<boolean>>(null);
  const contextKeyRef = useRef<Maybe<string>>(null);
  const isLoadCloningEnabled = serviceFeaturesEnabled.isLoadCloningEnabled;
  const isLoadBuildingEnabled = serviceFeaturesEnabled.isLoadBuildingEnabled;
  const isTruckListEnabled = serviceFeaturesEnabled.isTruckListEnabled;
  const isCarrierNetworkQuotingEnabled =
    serviceFeaturesEnabled.isCarrierNetworkQuotingEnabled;
  const quickQuoteUiEnabled = isQuickQuoteUiEnabled(serviceFeaturesEnabled);

  useEffect(() => {
    if (!enabled || !isLoadCloningEnabled) return;
    const refresh = () => {
      const key = `${email?.id ?? ''}:${email?.threadID ?? ''}`;
      if (cloneInFlightKeyRef.current !== key) {
        setCloneRevalidation((value) => value + 1);
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    // Like ordinary suggestions, the pulse only needs a focus refresh; QuoteSidebar polls
    // while clone work is active.
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, email?.id, email?.threadID, isLoadCloningEnabled]);

  useEffect(() => {
    const wasEnabled = previousCloningEnabledRef.current;
    previousCloningEnabledRef.current = isLoadCloningEnabled;
    if (wasEnabled && !isLoadCloningEnabled) {
      forceRefreshRef.current = true;
    }
  }, [isLoadCloningEnabled]);

  const contextKey = `${email?.id ?? ''}:${email?.threadID ?? ''}`;
  if (contextKeyRef.current !== contextKey) {
    contextKeyRef.current = contextKey;
    ordinarySignalRef.current = null;
    cloneSignalRef.current = null;
  }

  useEffect(() => {
    if (!enabled || !email?.threadID) {
      return;
    }

    // Already resolved for this thread (QuoteSidebar visit or prior prefetch).
    if (
      resolvedRef.current !== null &&
      !isLoadCloningEnabled &&
      !forceRefreshRef.current
    ) {
      return;
    }
    forceRefreshRef.current = false;

    let cancelled = false;
    const requestKey = contextKey;

    const run = async () => {
      const threadID = email.threadID;
      const emailID = email.id;

      const ordinarySuggestionsPromise = Promise.all([
        quickQuoteUiEnabled
          ? getQuickQuoteSuggestion(threadID)
          : Promise.resolve(null),
        isLoadBuildingEnabled
          ? getLoadBuildingSuggestion(threadID)
          : Promise.resolve(null),
        isTruckListEnabled && emailID
          ? getTruckList(emailID, threadID)
          : Promise.resolve(null),
        getQuoteRequest(threadID),
      ]);
      const [qqRes, lbRes, truckRes, qrRes] = await ordinarySuggestionsPromise;

      if (cancelled || contextKeyRef.current !== requestKey) {
        return;
      }

      const quoteSuggestions =
        qqRes && qqRes.isOk() ? filterInvalidSuggestions(qqRes.value) : [];
      const loadBuildingSuggestions =
        lbRes && lbRes.isOk()
          ? filterInvalidSuggestions(normalizeSuggestions(lbRes.value))
          : [];
      const truckListResult =
        truckRes && truckRes.isOk() ? truckRes.value : null;
      const quoteRequest = qrRes.isOk() ? qrRes.value : null;

      const nextHasQuoteViewFeatureSuggestions = hasQuoteViewSuggestions(
        {
          isCarrierNetworkQuotingEnabled,
          isTruckListEnabled,
        },
        quoteSuggestions,
        loadBuildingSuggestions,
        truckListResult,
        quoteRequest
      );
      ordinarySignalRef.current = nextHasQuoteViewFeatureSuggestions;

      setCurrentState((prev) => ({
        ...prev,
        hasQuoteViewFeatureSuggestions: combineSuggestionSignals(
          nextHasQuoteViewFeatureSuggestions,
          cloneSignalRef.current,
          prev.hasQuoteViewFeatureSuggestions
        ),
      }));
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [
    contextKey,
    email?.id,
    email?.threadID,
    enabled,
    isCarrierNetworkQuotingEnabled,
    isLoadBuildingEnabled,
    isLoadCloningEnabled,
    isTruckListEnabled,
    quickQuoteUiEnabled,
    setCurrentState,
  ]);

  useEffect(() => {
    if (!enabled || !email?.threadID || !isLoadCloningEnabled) {
      cloneSignalRef.current = false;
      return;
    }

    let cancelled = false;
    const requestKey = contextKey;
    cloneInFlightKeyRef.current = requestKey;

    void getLoadCloneRecommendations(email.threadID)
      .then((cloneRes) => {
        if (
          cancelled ||
          contextKeyRef.current !== requestKey ||
          !cloneRes.isOk()
        ) {
          return;
        }

        const hasCloneRecommendation = cloneRes.value.some(
          ({ disposition }) =>
            disposition === LoadCloneRecommendationDisposition.Pending ||
            disposition === LoadCloneRecommendationDisposition.Started
        );
        cloneSignalRef.current = hasCloneRecommendation;
        setCurrentState((prev) => ({
          ...prev,
          hasQuoteViewFeatureSuggestions: combineSuggestionSignals(
            ordinarySignalRef.current,
            hasCloneRecommendation,
            prev.hasQuoteViewFeatureSuggestions
          ),
        }));
      })
      .finally(() => {
        if (cloneInFlightKeyRef.current === requestKey) {
          cloneInFlightKeyRef.current = null;
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    cloneRevalidation,
    contextKey,
    email?.threadID,
    enabled,
    isLoadCloningEnabled,
    setCurrentState,
  ]);
}

function combineSuggestionSignals(
  ordinary: Maybe<boolean>,
  clone: Maybe<boolean>,
  previous: Maybe<boolean>
): Maybe<boolean> {
  if (ordinary === true || clone === true) return true;
  if (ordinary === null || clone === null) return previous;
  return false;
}
